import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import Carregando from '@/components/Carregando';
import { useLeads } from '@/leads/LeadsProvider';
import { saldoDoLead, temSaldoAberto } from '@/leads/recebimentos';

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const moedaCheia = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const paraData = (v) => (typeof v?.toDate === 'function' ? v.toDate() : null);
const dataCurta = (v) => {
  const d = paraData(v);
  return d ? format(d, 'dd/MM/yy') : '—';
};

/** Dias desde o último recebimento; null quando nunca houve. */
function diasParado(lead, agora = new Date()) {
  const d = paraData(lead.ultimoRecebimentoEm) ?? paraData(lead.dataFechamento);
  if (!d) return null;
  return Math.max(0, Math.floor((agora.getTime() - d.getTime()) / 86_400_000));
}

/**
 * Em aberto (WG) — quem ainda deve, de qualquer mês.
 *
 * ★★ ESTA TELA EXISTE PORQUE A OUTRA MUDOU DE PERGUNTA.
 *
 * Até 28/set/2026, `/recebimentos` respondia "quem ainda me deve" — e não os
 * recebimentos do mês, apesar do nome. Quando ela passou a ser o que o nome diz,
 * esta resposta precisou de casa própria (decisão do dono: saída B do plano, rota
 * em vez de segunda seção).
 *
 * ★ POR QUE NÃO PODIA VIRAR SÓ UMA COLUNA na lista do mês: quem não pagou nada
 * no mês **não tem linha lá**. Medido em 28/set: recortando agosto ou julho, os
 * 8 devedores desapareciam por completo; e em 1º de outubro, com o mês virado,
 * a lista do mês fica vazia enquanto R$ 22.593,34 seguem a receber — dois deles
 * abertos desde **fevereiro**.
 *
 * O critério é o mesmo de sempre, e a regra é a que já existia
 * (`leads/recebimentos.js:59-66`, com 25 casos de teste): lead na etapa
 * `fechado` cujo `valorFechado` é maior que o `valorRecebido`. A lista sai dos
 * leads já em memória (`LeadsProvider`) — nenhuma consulta nova, nenhum índice.
 *
 * ★ Aqui NÃO se lança recebimento. Ele entra só pela importação da planilha
 * (decisão do dono na WG); esta tela é de conferência.
 */
export default function RecebimentosEmAbertoPage() {
  const { leads, carregando } = useLeads();
  const [ordem, setOrdem] = useState('saldo'); // 'saldo' | 'parado'

  const emAberto = useMemo(() => {
    const lista = (leads ?? []).filter(temSaldoAberto);
    return lista.sort((a, b) =>
      ordem === 'saldo'
        ? saldoDoLead(b) - saldoDoLead(a)
        : (diasParado(b) ?? -1) - (diasParado(a) ?? -1),
    );
  }, [leads, ordem]);

  const totais = useMemo(
    () => ({
      contratos: emAberto.length,
      total: emAberto.reduce((t, l) => t + Number(l.valorFechado ?? 0), 0),
      recebido: emAberto.reduce((t, l) => t + Number(l.valorRecebido ?? 0), 0),
      saldo: emAberto.reduce((t, l) => t + saldoDoLead(l), 0),
    }),
    [emAberto],
  );

  if (carregando) return <Carregando texto="Carregando os contratos…" />;

  return (
    <div className="pagina">
      <header className="pagina-cabecalho com-acao">
        <div>
          <h1>Em aberto</h1>
          <p className="painel-sub">
            {totais.contratos} contrato{totais.contratos === 1 ? '' : 's'} com valor a pagar ·{' '}
            <strong>{moedaCheia(totais.saldo)}</strong> a receber de {moeda(totais.total)} vendidos
          </p>
        </div>
        <select value={ordem} onChange={(e) => setOrdem(e.target.value)} aria-label="Ordenar por">
          <option value="saldo">Maior valor a pagar primeiro</option>
          <option value="parado">Parado há mais tempo</option>
        </select>
      </header>

      {emAberto.length === 0 ? (
        <p className="vazio">Nenhum contrato com valor a pagar. Todo mundo em dia.</p>
      ) : (
        <div className="tabela-envolve">
          <table className="tabela tabela-recebimentos">
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Código Paciente</th>
                <th>Total</th>
                <th>Entrada</th>
                <th>Recebido</th>
                {/* ★ WD-c2 (item 4): mesmo rótulo da ficha — a mesma conta não pode ter dois nomes. */}
                <th>Valor a pagar</th>
                <th>Último</th>
              </tr>
            </thead>
            <tbody>
              {emAberto.map((lead) => {
                const dias = diasParado(lead);
                return (
                  <tr key={lead.id}>
                    <td>
                      <strong>{lead.nome || 'Sem nome'}</strong>
                      {lead.negociadorNome && (
                        <span className="indicador-nota"> · {lead.negociadorNome}</span>
                      )}
                    </td>
                    <td>{lead.codigoCareflow || '—'}</td>
                    <td>{moedaCheia(lead.valorFechado)}</td>
                    <td>{lead.valorEntrada ? moedaCheia(lead.valorEntrada) : '—'}</td>
                    <td>{lead.valorRecebido ? moedaCheia(lead.valorRecebido) : '—'}</td>
                    <td className="saldo-aberto">{moedaCheia(saldoDoLead(lead))}</td>
                    <td>
                      {dataCurta(lead.ultimoRecebimentoEm)}
                      {dias !== null && (
                        <span className={dias >= 30 ? 'indicador-nota alerta' : 'indicador-nota'}>
                          {' '}
                          · {dias}d
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2}>Total</th>
                <th>{moeda(totais.total)}</th>
                <th />
                <th>{moeda(totais.recebido)}</th>
                <th>{moedaCheia(totais.saldo)}</th>
                <th />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
